import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import { MessageCircle, CornerDownRight, Trash2, Shield, Send } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

// Flatten the nested comment tree into an ordered list with depth (pre-order DFS).
// Done iteratively to avoid a self-referencing recursive component.
function flattenTree(roots) {
  const out = [];
  const stack = [...[...roots].reverse().map((n) => ({ node: n, depth: 0 }))];
  while (stack.length) {
    const { node, depth } = stack.pop();
    out.push({ node, depth });
    const kids = node.replies || [];
    for (let i = kids.length - 1; i >= 0; i--) {
      stack.push({ node: kids[i], depth: depth + 1 });
    }
  }
  return out;
}

function CommentForm({ onSubmit, placeholder, onCancel, testid, autoFocus }) {
  const { t } = useLanguage();
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true);
    const ok = await onSubmit(body.trim());
    setBusy(false);
    if (ok) setBody("");
  };

  return (
    <form onSubmit={submit} className="mt-2">
      <textarea
        data-testid={testid}
        rows={2}
        value={body}
        autoFocus={autoFocus}
        onChange={(e) => setBody(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none p-2.5 resize-y bg-white"
      />
      <div className="flex items-center gap-2 mt-1.5">
        <button
          type="submit"
          disabled={busy || !body.trim()}
          data-testid={`${testid}-submit`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-xs font-medium transition-colors"
        >
          <Send size={13} /> {busy ? t("comments.posting") : t("comments.post")}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700">
            {t("comments.cancel")}
          </button>
        )}
      </div>
    </form>
  );
}

export default function CommentsSection({ quiz, questionId }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [comments, setComments] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/quiz/${quiz}/questions/${questionId}/comments`);
      setComments(data.comments || []);
      setIsAdmin(!!data.is_admin);
      setLoaded(true);
    } catch (e) {
      setLoaded(true);
    }
  }, [quiz, questionId]);

  useEffect(() => {
    if (open && !loaded) load();
  }, [open, loaded, load]);

  const countTop = comments.length;
  const flat = flattenTree(comments);

  const post = async (body, parentId = null) => {
    try {
      const { data } = await api.post(`/quiz/${quiz}/questions/${questionId}/comments`, {
        body,
        parent_id: parentId,
      });
      if (data.status === "approved") {
        await load();
      } else {
        toast.success(t("comments.pendingNotice"));
      }
      return true;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      return false;
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("comments.deleteConfirm"))) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      await load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-100">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`comments-toggle-${questionId}`}
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-teal-700 transition-colors"
      >
        <MessageCircle size={14} /> {t("comments.title")}
        {countTop > 0 && (
          <span className="inline-grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-teal-600 text-white text-[10px] font-semibold">
            {countTop}
          </span>
        )}
        <span className="text-slate-300">·</span>
        <span className="normal-case tracking-normal text-[11px]">
          {open ? t("comments.hideThread") : t("comments.showThread")}
        </span>
      </button>

      {open && (
        <div data-testid={`comments-panel-${questionId}`} className="mt-2">
          {loaded && flat.length === 0 && (
            <p className="text-xs text-slate-400 italic">{t("comments.none")}</p>
          )}
          {flat.map(({ node, depth }) => (
            <div
              key={node.id}
              data-testid="comment-node"
              className="mt-3"
              style={{ marginLeft: depth > 0 ? Math.min(depth, 6) * 16 : 0 }}
            >
              <div className={`rounded-lg bg-slate-50 border p-3 ${depth > 0 ? "border-l-2 border-l-slate-200 border-slate-200" : "border-slate-200"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-slate-800">{node.author_name}</span>
                  {node.is_admin && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-teal-700 bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">
                      <Shield size={10} /> {t("comments.adminBadge")}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-400">{timeAgo(node.created_at)}</span>
                </div>
                <p className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap break-words">{node.body}</p>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => setReplyOpen(replyOpen === node.id ? null : node.id)}
                    data-testid={`comment-reply-btn-${node.id}`}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal-700 transition-colors"
                  >
                    <CornerDownRight size={13} /> {t("comments.reply")}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => del(node.id)}
                      data-testid={`comment-delete-btn-${node.id}`}
                      className="inline-flex items-center gap-1 text-xs text-rose-500 hover:text-rose-700 transition-colors"
                    >
                      <Trash2 size={13} /> {t("comments.delete")}
                    </button>
                  )}
                </div>
                {replyOpen === node.id && (
                  <CommentForm
                    autoFocus
                    testid={`comment-reply-form-${node.id}`}
                    placeholder={t("comments.replyPlaceholder")}
                    onCancel={() => setReplyOpen(null)}
                    onSubmit={async (body) => {
                      const ok = await post(body, node.id);
                      if (ok) setReplyOpen(null);
                      return ok;
                    }}
                  />
                )}
              </div>
            </div>
          ))}
          {user && (
            <div className="mt-4">
              <CommentForm
                testid={`comment-form-${questionId}`}
                placeholder={t("comments.placeholder")}
                onSubmit={(body) => post(body, null)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
